import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Step from '@mui/material/Step';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import StepLabel from '@mui/material/StepLabel';

import PlusIcon from '@mui/icons-material/Close';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { IconButton } from '@mui/material';
import Modal from '@mui/material/Modal';
import { useNavigate } from 'react-router-dom';
import {
  useLazyGetApiProgramsQuery,
  useLazyGetApiUsersQuery,
  useLazyGetApiLocationsQuery,
  usePostApiAttachmentsMutation,
  usePostApiProjectsMutation,
  usePatchApiProjectsByIdMutation,
} from '../../../app/athenaApiGen';
import { athenaApi } from '../../../app/athenaApi';
import {
  clearErrors,
  closeModal,
  initializeAttachmentStatus,
  resetProjectModal,
  setActiveStep,
  setErrors,
  setFormData,
  setIsSubmitting,
  setLastCreatedProject,
  setOwnerName,
  setProgramName,
  setLocationName,
  updateAttachmentStatus,
} from '../../../app/projects/projectsSlice';
import AttachmentForm, { AttachmentContext } from './components/AttachmentForm';
import DeliverableTypesForm from './components/DeliverableTypesForm';
import Info from './components/Info';
import ProjectForm from './components/ProjectForm';
import Review from './components/Review';
import { useProjectPermissions } from '../useProjectPermissions';

/**
 * CardModal - Create New Project Modal
 *
 * Multi-step form for creating a new project with the following steps:
 * 1. Project Info Form - Basic project details
 * 2. Attachment Details - File uploads (optional)
 * 3. Review - Confirm before submission
 *
 * Form Data Structure (matches API HandlersCreateProjectRequest):
 * {
 *   title: string (REQUIRED) - Project title
 *   projectCode: string (REQUIRED) - Unique code (e.g., PROJ-2025-001)
 *   programId: string (REQUIRED) - UUID of the parent program
 *   ownerId: string (REQUIRED) - UUID of the project owner
 *   description: string (OPTIONAL) - Project description
 *   projectFilePath: string (OPTIONAL) - File storage path
 *   attachments: array (OPTIONAL) - Files to upload separately
 * }
 */

const getSteps = (mode) => [
  'Project Info Form',
  'Assign Deliverable Types',
  'Attachment details',
  mode === 'edit' ? 'Review Project Changes' : 'Review your New Project',
];

function getStepContent(step) {
  switch (step) {
    case 0:
      return <ProjectForm />;
    case 1:
      return <DeliverableTypesForm />;
    case 2:
      return <AttachmentForm />;
    case 3:
      return <Review />;
    default:
      throw new Error('Unknown step');
  }
}

const modalStyle = {
  position: 'absolute',
  height: { xs: '95vh', sm: '85vh', md: '80vh' },
  maxHeight: { xs: '100%', sm: '700px', md: '800px' },
  width: { xs: '95vw', sm: '90vw', md: '85vw', lg: '80vw' },
  maxWidth: { xs: '100%', sm: '900px', md: '1000px', lg: '1200px' },
  top: '50%',
  left: '50%',
  // Remove transform from here - it breaks backdrop positioning
  // transform: 'translate(-50%, -50%)',
  marginTop: { xs: '-47.5vh', sm: '-42.5vh', md: '-40vh' },
  marginLeft: { xs: '-47.5vw', sm: '-45vw', md: '-42.5vw', lg: '-40vw' },
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? '#a5c5e9ff' : '#fff',
  p: 0,
  boxShadow: 24,
  borderRadius: 2,
  border: 'none',
  overflow: 'hidden',
};

export default function CardModal() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get state from Redux store
  const {
    activeStep,
    formData,
    errors,
    isModalOpen: open,
    isSubmitting,
    lastCreatedProject,
    attachmentUploadStatus: uploadStatus,
    modalMode,
    editingProjectId,
  } = useSelector((state) => state.projects);

  // Get login token from Redux state for API calls
  const loginToken = useSelector((state) => state.user.loginToken);

  // ACL permission checks
  // For create mode: check canCreateProject
  // For edit mode: check canEditProject with the specific project ID
  const { canCreateProject, hasProjectAction, isAdmin } = useProjectPermissions(
    editingProjectId,
    formData?.programId
  );

  // Check if user has permission for current mode
  const hasPermission = React.useMemo(() => {
    if (isAdmin) return true;
    if (modalMode === 'edit') {
      return hasProjectAction('update-project', editingProjectId);
    }
    return canCreateProject;
  }, [modalMode, editingProjectId, canCreateProject, hasProjectAction, isAdmin]);

  // Get steps based on mode
  const steps = getSteps(modalMode);

  // Check if any uploads are in progress
  const isUploading = uploadStatus.some((s) => s.status === 'uploading');

  // Local state for actual File objects and retry tracking
  const [attachmentFiles, setAttachmentFiles] = React.useState([]);
  const [isRetrying, setIsRetrying] = React.useState(false);

  const handleNavigateToProjects = () => {
    navigate('/projects'); // Navigate to the /projects route
  };

  const handleClose = () => {
    dispatch(closeModal());
  };

  // Fetch programs, users, and locations for displaying names in Info sidebar
  const [fetchPrograms, { data: programsData }] = useLazyGetApiProgramsQuery();
  const [fetchUsers, { data: usersData }] = useLazyGetApiUsersQuery();
  const [fetchLocations, { data: locationsData }] = useLazyGetApiLocationsQuery();

  // API mutation for creating project
  const [createProject, { isLoading: isCreating }] =
    usePostApiProjectsMutation();

  // API mutation for updating project
  const [updateProject, { isLoading: isUpdating }] =
    usePatchApiProjectsByIdMutation();

  // API mutation for uploading attachments
  const [uploadAttachment] = usePostApiAttachmentsMutation();

  // Fetch data when modal opens
  useEffect(() => {
    if (open) {
      fetchPrograms();
      fetchUsers();
      fetchLocations();
    }
  }, [open, fetchPrograms, fetchUsers, fetchLocations]);

  // Get display names for selected IDs and update Redux
  const programs = programsData || [];
  const users = usersData || [];
  const locations = locationsData || [];

  // Auto-assign programId if user has access to only one program (create mode only)
  useEffect(() => {
    if (open && modalMode === 'create' && programs.length === 1 && !formData.programId) {
      dispatch(setFormData({ programId: programs[0].programId }));
    }
  }, [open, modalMode, programs, formData.programId, dispatch]);
  const selectedProgram = programs.find(
    (p) => p.programId === formData.programId,
  );
  // Find user by user.id (formData.ownerId now stores user.id, not contact.contactId)
  const selectedUser = users.find((u) => u.id === formData.ownerId);
  const selectedLocation = locations.find(
    (l) => l.locationId === formData.locationId,
  );

  const programName = selectedProgram
    ? selectedProgram.contractCode
      ? `${selectedProgram.title} (${selectedProgram.contractCode})`
      : selectedProgram.title
    : '';
  const ownerName = selectedUser?.userName || selectedUser?.contact?.name || '';
  const locationName = selectedLocation?.name || selectedLocation?.locationCode || '';

  // Update Redux with computed names and creatorContactId when they change
  useEffect(() => {
    dispatch(setProgramName(programName));
    dispatch(setOwnerName(ownerName));
    dispatch(setLocationName(locationName));

    // Store creatorContactId when user is selected
    if (selectedUser?.contact?.contactId) {
      dispatch(
        setFormData({ creatorContactId: selectedUser.contact.contactId }),
      );
    }
  }, [programName, ownerName, locationName, selectedUser, dispatch]);

  // Reset form and steps when modal is closed
  useEffect(() => {
    if (!open) {
      dispatch(resetProjectModal());
      setAttachmentFiles([]); // Clear local file state
    }
  }, [open, dispatch]);

  // Close modal if user doesn't have permission
  useEffect(() => {
    if (open && !hasPermission) {
      console.warn(`User does not have permission to ${modalMode === 'edit' ? 'edit' : 'create'} project`);
      dispatch(closeModal());
    }
  }, [open, hasPermission, modalMode, dispatch]);

  // Validation function for each step
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Step 1: Project Info validation - API required fields
      if (!formData.title || formData.title.trim() === '') {
        newErrors.title = 'Project title is required';
      }
      if (!formData.projectCode || formData.projectCode.trim() === '') {
        newErrors.projectCode = 'Project code is required';
      }
      if (!formData.programId) {
        newErrors.programId = 'Program is required';
      }
      if (!formData.ownerId) {
        newErrors.ownerId = 'Owner is required';
      }
      // description and projectFilePath are optional
    }

    // Step 2: Attachments validation (optional, can add file size/type checks)
    // No required validation for attachments currently

    dispatch(setErrors(newErrors));
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (validateStep(activeStep)) {
      dispatch(setActiveStep(activeStep + 1));
    }
  };

  const handleBack = () => {
    dispatch(setActiveStep(Math.max(activeStep - 1, 0)));
    // Clear errors when going back
    dispatch(clearErrors());
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Final validation before submission
    if (!validateStep(0)) {
      dispatch(setActiveStep(0)); // Go back to first step if validation fails
      return;
    }

    dispatch(setIsSubmitting(true));
    try {
      // Find the selected user to get their contact.contactId
      const selectedUser = users.find((u) => u.id === formData.ownerId);

      if (!selectedUser?.contact?.contactId) {
        throw new Error('Selected user does not have a valid contact ID');
      }

      // Prepare API payload - only include fields that match CreateProjectRequest
      const projectPayload = {
        title: formData.title,
        projectCode: formData.projectCode,
        programId: formData.programId,
        ownerId: selectedUser.contact.contactId, // Map user.id -> contact.contactId for API
      };

      // Add optional fields only if they have values
      if (formData.description && formData.description.trim() !== '') {
        projectPayload.description = formData.description;
      }
      if (formData.projectFilePath && formData.projectFilePath.trim() !== '') {
        projectPayload.projectFilePath = formData.projectFilePath;
      }
      if (formData.locationId && formData.locationId.trim() !== '') {
        projectPayload.locationId = formData.locationId;
      }

      let result;

      // Call appropriate API based on mode
      if (modalMode === 'edit' && editingProjectId) {
        result = await updateProject({
          id: editingProjectId,
          handlersUpdateProjectRequest: projectPayload,
        }).unwrap();
      } else {
        result = await createProject({
          handlersCreateProjectRequest: projectPayload,
        }).unwrap();
      }

      // Store created project in Redux state
      dispatch(setLastCreatedProject(result));

      // Associate selected deliverable types with the project
      if (formData.selectedDeliverableTypes && formData.selectedDeliverableTypes.length > 0) {
        // console.log('Associating Deliverable Types:', formData.selectedDeliverableTypes);
        // console.log('Project ID:', result.projectId);
        // console.log('🔐 Using token for deliverable types request:', loginToken ? 'Token exists' : 'NO TOKEN');
        
        try {
          // Use PUT for edit mode to replace all deliverable types, POST for create mode
          const method = modalMode === 'edit' ? 'PUT' : 'POST';
          const response = await fetch(`/api/projects/${result.projectId}/deliverable-types`, {
            method: method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${loginToken}`,
            },
            body: JSON.stringify({
              deliverableTypeIds: formData.selectedDeliverableTypes,
            }),
          });

          if (!response.ok) {
            console.error(`Failed to ${modalMode === 'edit' ? 'update' : 'associate'} deliverable types:`, response.statusText);
            // Don't fail the entire operation if deliverable type association fails
            // The project is already created/updated successfully
          } else {
            // console.log(`Successfully ${modalMode === 'edit' ? 'updated' : 'associated'} deliverable types with project`);
          }
        } catch (error) {
          console.error('Error managing deliverable types:', error);
          // Don't fail the entire operation if deliverable type association fails
        }
      } else if (modalMode === 'edit') {
        // In edit mode, if no deliverable types are selected, remove all associations
        // console.log('🔐 Using token to remove deliverable types:', loginToken ? 'Token exists' : 'NO TOKEN');
        
        try {
          const response = await fetch(`/api/projects/${result.projectId}/deliverable-types`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${loginToken}`,
            },
            body: JSON.stringify({
              deliverableTypeIds: [],
            }),
          });

          if (!response.ok) {
            console.error('Failed to remove deliverable types:', response.statusText);
          } else {
            // console.log('Successfully removed all deliverable types from project');
          }
        } catch (error) {
          console.error('Error removing deliverable types:', error);
        }
      }

      // Initialize attachment upload status tracking
      if (attachmentFiles && attachmentFiles.length > 0) {
        const filenames = attachmentFiles.map((f) => f.file.name);
        dispatch(initializeAttachmentStatus(filenames));
      }

      // Upload attachments if any exist
      const attachmentResults = {
        successful: [],
        failed: [],
      };

      if (attachmentFiles && attachmentFiles.length > 0) {
        for (const fileObj of attachmentFiles) {
          try {
            // Update status to uploading
            dispatch(
              updateAttachmentStatus({
                filename: fileObj.file.name,
                status: 'uploading',
              }),
            );

            const uploadFormData = new FormData();
            uploadFormData.append('objectId', result.projectId);
            uploadFormData.append('objectType', 'Project');
            uploadFormData.append('name', fileObj.file.name);
            uploadFormData.append(
              'creatorContactId',
              selectedUser.contact.contactId,
            );
            // formSectionId is optional - API will use default "PROJECT TITLE" section if not provided

            // Add description if provided
            if (fileObj.description && fileObj.description.trim() !== '') {
              uploadFormData.append('description', fileObj.description);
            }

            uploadFormData.append('file', fileObj.file);

            await uploadAttachment({
              body: uploadFormData,
            }).unwrap();

            // Update status to success
            dispatch(
              updateAttachmentStatus({
                filename: fileObj.file.name,
                status: 'success',
              }),
            );

            attachmentResults.successful.push(fileObj.file.name);
          } catch (attachmentError) {
            console.error(
              `Error uploading attachment ${fileObj.file.name}:`,
              attachmentError,
            );
            const errorMsg =
              attachmentError?.data?.details ||
              attachmentError?.data?.message ||
              attachmentError?.message ||
              'Unknown error';

            // Update status to error
            dispatch(
              updateAttachmentStatus({
                filename: fileObj.file.name,
                status: 'error',
                error: errorMsg,
              }),
            );

            attachmentResults.failed.push({
              name: fileObj.file.name,
              error: errorMsg,
            });
          }
        }
      }

      // Store attachment upload results for display in success screen
      dispatch(
        setLastCreatedProject({
          ...result,
          attachmentResults,
        }),
      );

      // Invalidate caches to refresh the projects table
      // Always invalidate Attachments cache in edit mode (attachments may have been deleted)
      // Invalidate Projects cache when new attachments were uploaded successfully
      if (modalMode === 'edit') {
        dispatch(athenaApi.util.invalidateTags(['Attachments']));
      }
      if (attachmentFiles && attachmentFiles.length > 0 && attachmentResults.successful.length > 0) {
        dispatch(athenaApi.util.invalidateTags(['Projects', 'Attachments']));
      }

      // Move to success step
      dispatch(setActiveStep(steps.length));
    } catch (error) {
      console.error(`Error ${modalMode === 'edit' ? 'updating' : 'creating'} project:`, error);

      // Extract detailed error message from API response
      // API returns: { error: "Conflict", details: "Project code already exists" }
      const errorMessage =
        error?.data?.details ||
        error?.data?.message ||
        error?.message ||
        `Failed to ${modalMode === 'edit' ? 'update' : 'create'} project. Please try again.`;
      dispatch(setErrors({ submit: errorMessage }));
    } finally {
      dispatch(setIsSubmitting(false));
    }
  };

  // Retry failed attachment uploads
  const handleRetryFailedUploads = async () => {
    if (!lastCreatedProject?.projectId) return;

    setIsRetrying(true);

    try {
      const failedFiles = lastCreatedProject.attachmentResults.failed.map(
        (f) => f.name,
      );
      const filesToRetry = attachmentFiles.filter((fileObj) =>
        failedFiles.includes(fileObj.file.name),
      );

      if (filesToRetry.length === 0) {
        setIsRetrying(false);
        return;
      }

      // Reset status for failed files to pending
      filesToRetry.forEach((fileObj) => {
        dispatch(
          updateAttachmentStatus({
            filename: fileObj.file.name,
            status: 'pending',
          }),
        );
      });

      const retryResults = {
        successful: [
          ...(lastCreatedProject.attachmentResults.successful || []),
        ],
        failed: [],
      };

      // Retry uploading only failed files
      for (const fileObj of filesToRetry) {
        try {
          dispatch(
            updateAttachmentStatus({
              filename: fileObj.file.name,
              status: 'uploading',
            }),
          );

          const uploadFormData = new FormData();
          uploadFormData.append('objectId', lastCreatedProject.projectId);
          uploadFormData.append('objectType', 'Project');
          uploadFormData.append('name', fileObj.file.name);
          uploadFormData.append('creatorContactId', formData.creatorContactId);

          if (fileObj.description && fileObj.description.trim() !== '') {
            uploadFormData.append('description', fileObj.description);
          }

          uploadFormData.append('file', fileObj.file);

          await uploadAttachment({
            body: uploadFormData,
          }).unwrap();

          dispatch(
            updateAttachmentStatus({
              filename: fileObj.file.name,
              status: 'success',
            }),
          );

          retryResults.successful.push(fileObj.file.name);
        } catch (error) {
          console.error(
            `Error retrying upload for ${fileObj.file.name}:`,
            error,
          );
          const errorMsg =
            error?.data?.details ||
            error?.data?.message ||
            error?.message ||
            'Unknown error';

          dispatch(
            updateAttachmentStatus({
              filename: fileObj.file.name,
              status: 'error',
              error: errorMsg,
            }),
          );

          retryResults.failed.push({
            name: fileObj.file.name,
            error: errorMsg,
          });
        }
      }

      // Update results
      dispatch(
        setLastCreatedProject({
          ...lastCreatedProject,
          attachmentResults: retryResults,
        }),
      );

      // Invalidate caches to refresh the projects table with updated attachment counts
      if (retryResults.successful.length > 0) {
        dispatch(athenaApi.util.invalidateTags(['Projects', 'Attachments']));
      }
    } catch (error) {
      console.error('Error during retry:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <AttachmentContext.Provider
      value={{ files: attachmentFiles, setFiles: setAttachmentFiles }}
    >
      <Modal
        open={open}
        onClose={handleClose}
        sx={modalStyle}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        container={() => document.body}
        slotProps={{
          backdrop: {
            sx: {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backdropFilter: 'blur(1px)',
              WebkitBackdropFilter: 'blur(1px)',
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', height: '100%' }}>
          <Box
            sx={{
              width: { xs: '100%', sm: '35%', md: '38%', lg: '33.333333%' },
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.paper',
              borderRight: { sm: 'none', md: '1px solid' },
              borderColor: { sm: 'none', md: 'divider' },
              alignItems: 'start',
              pt: 2,
              px: { xs: 2, sm: 1.5, md: 2 },
              gap: 2,
              height: '100%',
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                {modalMode === 'edit' ? 'Edit Project' : 'New Project'}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minHeight: 0,
                width: '100%',
                maxWidth: 600,
                overflow: 'hidden',
              }}
            >
              <Info />
            </Box>
          </Box>
          <Box
            sx={{
              width: { xs: '100%', sm: '65%', md: '62%', lg: '66.666667%' },
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '100%',
              alignItems: 'start',
              pt: { xs: 0, sm: 3, md: 4, lg: 5 },
              px: { xs: 2, sm: 2, md: 3, lg: 5 },
              gap: { xs: 2, md: 2 },
              backgroundColor: 'background.default',
              height: '100%',
              overflow: 'auto',
            }}
          >
            <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation(); // <-- stop propagation to parent component
                  handleClose();
                }}
                size="small"
                sx={{
                  minWidth: 0,
                  padding: 1,
                  borderRadius: '50%',
                  background: 'rgba(0, 0, 0, 0)',
                  '&:hover': {
                    background: 'rgba(0, 0, 0, 0)',
                    color: 'rgb(0, 136, 255)',
                  },
                  color: (theme) => theme.palette.primary,
                }}
              >
                <PlusIcon />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { sm: 'space-between', md: 'flex-end' },
                alignItems: 'center',
                width: '100%',
                maxWidth: { sm: '100%', md: 600 },
              }}
            >
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  flexGrow: 1,
                }}
              >
                <Stepper
                  id="desktop-stepper"
                  activeStep={activeStep}
                  sx={{ width: '100%', height: 40 }}
                >
                  {steps.map((label) => (
                    <Step
                      sx={{
                        ':first-of-type': { pl: 0 },
                        ':last-child': { pr: 0 },
                      }}
                      key={label}
                    >
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                width: '100%',
                maxWidth: { xs: '100%', sm: '100%', md: '550px', lg: '600px' },
                gap: { xs: 3, md: 'none' },
                overflow: 'auto',
                minHeight: 0,
              }}
            >
              <Stepper
                id="mobile-stepper"
                activeStep={activeStep}
                alternativeLabel
                sx={{ display: { sm: 'flex', md: 'none' } }}
              >
                {steps.map((label) => (
                  <Step
                    sx={{
                      ':first-of-type': { pl: 0 },
                      ':last-child': { pr: 0 },
                      '& .MuiStepConnector-root': { top: { xs: 6, sm: 12 } },
                    }}
                    key={label}
                  >
                    <StepLabel
                      sx={{
                        '.MuiStepLabel-labelContainer': { maxWidth: '70px' },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              {activeStep === steps.length ? (
                <Stack spacing={2} useFlexGap>
                  <Typography variant="h1"></Typography>
                  <Typography variant="h5">
                    {modalMode === 'edit'
                      ? 'You have successfully updated the project!'
                      : 'You have successfully created a project!'}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', mb: 2 }}
                  >
                    <strong>Project Code:</strong> {formData.projectCode}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', mb: 2 }}
                  >
                    <strong>Program:</strong> {programName}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', mb: 2 }}
                  >
                    <strong>Project Owner:</strong> {ownerName}
                  </Typography>
                  {modalMode === 'edit' && lastCreatedProject?.lastModified && (
                    <Typography
                      variant="body1"
                      sx={{ color: 'text.secondary', mb: 2 }}
                    >
                      <strong>Last Modified:</strong>{' '}
                      {new Date(lastCreatedProject.lastModified).toLocaleString()}
                    </Typography>
                  )}

                  {/* Display attachment upload results */}
                  {lastCreatedProject?.attachmentResults && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      {lastCreatedProject.attachmentResults.successful.length >
                        0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ color: 'success.main', mb: 0.5 }}
                          >
                            <strong>
                              ✓ Successfully uploaded{' '}
                              {
                                lastCreatedProject.attachmentResults.successful
                                  .length
                              }{' '}
                              attachment(s):
                            </strong>
                          </Typography>
                          <Box
                            component="ul"
                            sx={{
                              mt: 0.5,
                              mb: 0,
                              pl: 3,
                              color: 'success.main',
                            }}
                          >
                            {lastCreatedProject.attachmentResults.successful.map(
                              (fileName, index) => (
                                <Box
                                  component="li"
                                  key={index}
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {fileName}
                                </Box>
                              ),
                            )}
                          </Box>
                        </Box>
                      )}
                      {lastCreatedProject.attachmentResults.failed.length >
                        0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ color: 'error.main', mb: 0.5 }}
                          >
                            <strong>
                              ✗ Failed to upload{' '}
                              {
                                lastCreatedProject.attachmentResults.failed
                                  .length
                              }{' '}
                              attachment(s):
                            </strong>
                          </Typography>
                          <Box
                            component="ul"
                            sx={{
                              mt: 0.5,
                              mb: 1,
                              pl: 3,
                              color: 'error.main',
                            }}
                          >
                            {lastCreatedProject.attachmentResults.failed.map(
                              (item, index) => (
                                <Box
                                  component="li"
                                  key={index}
                                  sx={{ fontSize: '0.875rem' }}
                                >
                                  {item.name} - {item.error}
                                </Box>
                              ),
                            )}
                          </Box>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleRetryFailedUploads}
                            disabled={isRetrying}
                            startIcon={
                              isRetrying ? <CircularProgress size={16} /> : null
                            }
                            sx={{ mt: 1 }}
                          >
                            {isRetrying
                              ? 'Retrying...'
                              : 'Retry Failed Uploads'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}

                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                  >
                    Note: A notification will be sent out once the notification
                    service is set up.
                  </Typography>
                  <Button
                    onClick={() => {
                      handleNavigateToProjects();
                      handleClose();
                    }}
                    variant="filled"
                    sx={{
                      alignSelf: 'start',
                      width: { xs: '100%', sm: 'auto' },
                      background: 'rgb(31, 77, 131)',
                      '&:hover': {
                        background: 'rgb(0, 136, 255)',
                      },
                      color: (theme) => theme.palette.secondary.contrastText,
                    }}
                  >
                    Go to my projects
                  </Button>
                </Stack>
              ) : (
                <React.Fragment>
                  {getStepContent(activeStep)}
                  {errors.submit && (
                    <Typography color="error" sx={{ mt: 2, mb: 1 }}>
                      {errors.submit}
                    </Typography>
                  )}
                  <Box
                    sx={[
                      {
                        display: 'flex',
                        flexDirection: { xs: 'column-reverse', sm: 'row' },
                        alignItems: 'end',
                        flexGrow: 1,
                        gap: 1,
                        pb: { xs: 12, sm: 0 },
                        mt: { xs: 2, sm: 0 },
                        mb: '60px',
                      },
                      activeStep !== 0
                        ? { justifyContent: 'space-between' }
                        : { justifyContent: 'flex-end' },
                    ]}
                  >
                    {activeStep !== 0 && (
                      <Button
                        startIcon={<ChevronLeftRoundedIcon />}
                        onClick={handleBack}
                        variant="text"
                        disabled={isUploading || isRetrying}
                        sx={{ display: { xs: 'none', sm: 'flex' } }}
                      >
                        Previous
                      </Button>
                    )}
                    {activeStep !== 0 && (
                      <Button
                        startIcon={<ChevronLeftRoundedIcon />}
                        onClick={handleBack}
                        variant="outlined"
                        fullWidth
                        disabled={isUploading || isRetrying}
                        sx={{ display: { xs: 'flex', sm: 'none' } }}
                      >
                        Previous
                      </Button>
                    )}
                    <Button
                      variant="filled"
                      endIcon={<ChevronRightRoundedIcon />}
                      onClick={
                        activeStep === steps.length - 1
                          ? handleSubmit
                          : handleNext
                      }
                      disabled={isSubmitting || isUploading || isRetrying}
                      sx={{
                        width: { xs: '100%', sm: 'fit-content' },
                        background: 'rgb(31, 77, 131)',
                        '&:hover': {
                          background: 'rgb(0, 136, 255)',
                        },
                        color: (theme) => theme.palette.secondary.contrastText,
                      }}
                    >
                      {isSubmitting || isUploading
                        ? `${modalMode === 'edit' ? 'Updating' : 'Submitting'}...`
                        : activeStep === steps.length - 1
                          ? `${modalMode === 'edit' ? 'Update' : 'Submit'} Project`
                          : 'Next'}
                    </Button>
                  </Box>
                </React.Fragment>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </AttachmentContext.Provider>
  );
}
